import React from "react";
import ReactDOM from "react-dom";

import * as Sentry from "@sentry/browser";

import Tracking from "@shared/tracking";
import StairsLoader from "@shared/StairsLoader";
import RegistrationWizard from "../packs/registration/RegistrationWizard";
import AddClient from "../packs/wealth_managers/AddClient";
import OrganizationSearchSelect from "../packs/shared/OrganizationSearchSelect";
import AddGroup from "../packs/wealth_managers/AddGroup";
import EditGroup from "../packs/wealth_managers/EditGroup";
import AddAsset from "../packs/onboarding/assets/AddAsset";
import DeleteAsset from "../packs/onboarding/assets/DeleteAsset";
import AssetForm from "../packs/onboarding/assets/forms/AssetForm";
import RiskProfileWidget from "../packs/risk_profile/RiskProfileWidget";
import SafeRetirement from "../packs/safe_retirement/SafeRetirement";
import SafeRetirementBar from "../packs/safe_retirement/SafeRetirementBar";
import SafeRetirementTextIndicator from "../packs/safe_retirement/SafeRetirementTextIndicator";
import SaveRetirementScenario from "@components/SaveRetirementScenario";
import ProfileWizard from "../packs/onboarding/profile/ProfileWizard";
import AdviceRecordModal from "../packs/advice_records/AdviceRecordModal";
import PhoneInput from "@shared/PhoneInput";
import { OnboardingRiskProfile } from "@containers/Risk/OnboardingRiskProfile";
import ProjectionCharts from "@components/ProjectionCharts";
import { ArchiveButton } from "@components/RailsViewButtons/ArchiveButton";
import { InviteButton } from "@components/RailsViewButtons/InviteButton";
import { MonthlyRetirementBudget } from "@components/MonthlyRetirementBudget";

// There are side effects in these files
import CurrencyFormatter from "../packs/input_formatting/currency_formatter";

// Pages
import { Astute } from "@containers/Astute";
import { AttendanceTracker } from "@containers/AttendanceTracker";
import { JointScenario } from "@containers/JointScenario";
import { MetricsDashboard } from "@containers/MetricsDashboard";
import { Navigation } from "@components/Navigation";
import { NewJointScenario } from "@containers/JointScenario/New";
import { OrganizationDashboard } from "@containers/OrganizationDashboard";
import { OrganizationDashboardExperience } from "@containers/OrganizationDashboardExperience";
import { FspAdminDashboard } from "@containers/FspAdminDashboard";
import { SalesDashboard } from "@containers/SalesDashboard";
import { Preferences } from "@containers/Preferences";
import { Reminders } from "@containers/Reminders";
import { Scenario } from "@containers/Scenario";
import { Scenarios } from "@containers/Scenarios";
import { SecuritySettings } from "@containers/SecuritySettings";
import { UserProfile } from "@containers/UserProfile";

const ReactComponentsRegistry = {
  ProjectionCharts,
  RegistrationWizard,
  AddClient,
  OrganizationSearchSelect,
  EditGroup,
  AddGroup,
  AddAsset,
  AssetForm,
  DeleteAsset,
  RiskProfileWidget,
  SafeRetirement,
  SafeRetirementBar,
  SafeRetirementTextIndicator,
  MonthlyRetirementBudget,
  SaveRetirementScenario,
  ProfileWizard,
  AdviceRecordModal,
  PhoneInput,
  StairsLoader,
  Reminders,
  OnboardingRiskProfile,
  Scenario,
  MetricsDashboard,
  Preferences,
  Astute,
  ArchiveButton,
  InviteButton,
  JointScenario,
  NewJointScenario,
  Navigation,
  Scenarios,
  SecuritySettings,
  OrganizationDashboard,
  OrganizationDashboardExperience,
  FspAdminDashboard,
  SalesDashboard,
  UserProfile,
  AttendanceTracker,
};

// Each element is mounted on its own. A component that throws while rendering
// used to take down every element after it in the document, so one broken card
// showed up as a blank page or a page with nothing under the navigation.
const renderReactComponents = () => {
  [].forEach.call(document.querySelectorAll(".react-element"), (element) => {
    const name = element.dataset.reactComponent;

    try {
      // Own properties only: `data-react-component="toString"` would otherwise
      // reach Object.prototype and mount an inherited function instead of
      // reporting an unknown component.
      if (!Object.prototype.hasOwnProperty.call(ReactComponentsRegistry, name)) {
        throw new Error(`No component registered under "${name}"`);
      }

      const ComponentToRender = ReactComponentsRegistry[name];

      ReactDOM.render(
        <ComponentToRender {...JSON.parse(element.dataset.reactProps || "{}")} />,
        element
      );
    } catch (error) {
      console.error(`Failed to mount React component "${name}"`, error);
      Sentry.captureException(error, { tags: { reactComponent: name } });
    }
  });
};

window.Sentry = Sentry;

const elementTracker = new Tracking();
const currencyFormatter = new CurrencyFormatter();

document.addEventListener("DOMContentLoaded", () => {
  renderReactComponents();
  elementTracker.bindTrackers();
});
